import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Spinner, Table } from "react-bootstrap";
import authService from "../../../utils/authService";

type ReleaseChannel = "staging" | "prod";
type ReleaseStatus = "built" | "released" | "deprecated";
type ReleasePlatform = "win" | "mac" | "linux";

interface PlatformSummary {
  platform: ReleasePlatform;
  status: ReleaseStatus;
  id: number;
  file_size: number;
  sha256: string;
}

interface GroupedRelease {
  version: string;
  channel: ReleaseChannel;
  platforms: PlatformSummary[];
  release_notes?: string | null;
  created_at: string;
  published_at?: string | null;
  can_release: boolean;
  can_rollback: boolean;
}

type PendingAction = {
  release: GroupedRelease;
  action: "release" | "rollback";
};

const CHANNELS: { value: ReleaseChannel; label: string }[] = [
  { value: "prod", label: "Production" },
  { value: "staging", label: "Staging" },
];

const statusBadgeClass = (status: ReleaseStatus): string => {
  if (status === "built") return "bg-warning text-dark";
  if (status === "released") return "bg-success";
  return "bg-secondary";
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatSize = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const channelTabClass = (active: boolean): string =>
  `btn btn-sm px-3 py-1 rounded-1 border me-2 ${
    active ? "bg-light text-dark fw-semibold border-secondary" : "bg-white text-muted"
  }`;

const DesktopAppReleases: React.FC = () => {
  const [channel, setChannel] = useState<ReleaseChannel>("prod");
  const [items, setItems] = useState<GroupedRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const fetchIdRef = useRef(0);

  const fetchReleases = useCallback(async (options?: { silent?: boolean }) => {
    const fetchId = ++fetchIdRef.current;
    if (!options?.silent) {
      setLoading(true);
      setError("");
    }

    try {
      const response = await authService.get(
        `admin/desktop-release/grouped?channel=${channel}`
      );
      if (fetchId !== fetchIdRef.current) return;
      setItems(response.data.items || []);
    } catch (err: unknown) {
      if (fetchId !== fetchIdRef.current) return;
      console.error(err);
      setError("Failed to load desktop releases.");
      setItems([]);
    } finally {
      if (fetchId === fetchIdRef.current && !options?.silent) {
        setLoading(false);
      }
    }
  }, [channel]);

  useEffect(() => {
    void fetchReleases();
  }, [fetchReleases]);

  const handleChannelChange = useCallback((nextChannel: ReleaseChannel) => {
    setChannel(nextChannel);
    setSuccessMessage("");
    setPendingAction(null);
  }, []);

  const openConfirm = useCallback((release: GroupedRelease, action: "release" | "rollback") => {
    setPendingAction({ release, action });
    setSuccessMessage("");
  }, []);

  const closeConfirm = useCallback(() => {
    if (actionLoading) return;
    setPendingAction(null);
  }, [actionLoading]);

  const handleConfirm = useCallback(async () => {
    if (!pendingAction) return;

    const { release, action } = pendingAction;
    setActionLoading(true);
    setError("");

    try {
      const path =
        action === "release"
          ? "admin/desktop-release/release"
          : "admin/desktop-release/rollback";
      await authService.post(path, { version: release.version, channel });
      setPendingAction(null);
      setSuccessMessage(
        action === "release"
          ? `v${release.version} released to ${channel}.`
          : `Rolled back to v${release.version} on ${channel}.`
      );
      await fetchReleases({ silent: true });
    } catch (err: unknown) {
      console.error(err);
      setError(
        action === "release"
          ? "Release failed. Check that Windows build exists with status built."
          : "Rollback failed."
      );
    } finally {
      setActionLoading(false);
    }
  }, [pendingAction, channel, fetchReleases]);

  const selectedRelease = pendingAction?.release ?? null;
  const confirmAction = pendingAction?.action ?? "release";

  const tableBody = useMemo(() => {
    const rows: React.ReactNode[] = [];

    if (successMessage) {
      rows.push(
        <tr key="success">
          <td colSpan={6} className="text-center text-success py-2">
            {successMessage}
          </td>
        </tr>
      );
    }

    if (loading) {
      rows.push(
        <tr key="loading">
          <td colSpan={6} className="text-center py-5">
            <Spinner animation="border" />
          </td>
        </tr>
      );
      return rows;
    }

    if (error) {
      rows.push(
        <tr key="error">
          <td colSpan={6} className="text-center text-danger py-4">
            {error}
          </td>
        </tr>
      );
      return rows;
    }

    if (items.length === 0) {
      rows.push(
        <tr key="empty">
          <td colSpan={6} className="text-center text-muted py-4">
            No releases found for {channel}.
          </td>
        </tr>
      );
      return rows;
    }

    items.forEach((row) => {
      rows.push(
        <tr key={`${row.channel}-${row.version}`}>
          <td className="fw-semibold">v{row.version}</td>
          <td>
            <div className="d-flex flex-wrap gap-1">
              {row.platforms.map((p) => (
                <span
                  key={p.platform}
                  className={`badge border ${statusBadgeClass(p.status)}`}
                  title={`${formatSize(p.file_size)} · ${p.sha256.slice(0, 12)}…`}
                >
                  {p.platform}: {p.status}
                </span>
              ))}
            </div>
          </td>
          <td className="text-muted small">{formatDate(row.created_at)}</td>
          <td className="text-muted small">{formatDate(row.published_at)}</td>
          <td className="small text-muted text-truncate">
            <span title={row.release_notes || ""}>{row.release_notes || "—"}</span>
          </td>
          <td className="text-end text-nowrap">
            {row.can_release && (
              <Button
                size="sm"
                variant="primary"
                className="me-2"
                onClick={() => openConfirm(row, "release")}
              >
                Release
              </Button>
            )}
            {row.can_rollback && (
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => openConfirm(row, "rollback")}
              >
                Rollback
              </Button>
            )}
          </td>
        </tr>
      );
    });

    return rows;
  }, [successMessage, loading, error, items, channel, openConfirm]);

  return (
    <div
      className="border rounded-2 bg-white my-2 me-2 d-flex flex-column"
      style={{ height: "calc(100vh - 88px)", overflow: "hidden" }}
    >
      <div className="flex-grow-1 overflow-auto p-3 d-flex flex-column">
        <h5 className="mb-1">Desktop App Releases</h5>
        <p className="text-muted small mb-3">
          Review built versions and release or rollback the desktop app for students.
        </p>

        <div className="d-flex mb-3" role="group" aria-label="Release channel">
          {CHANNELS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={channelTabClass(channel === value)}
              onClick={() => handleChannelChange(value)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="table-responsive flex-grow-1">
          <Table striped bordered hover size="sm" className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>Version</th>
                <th>Platforms</th>
                <th>Created</th>
                <th>Published</th>
                <th className="w-25">Release notes</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>{tableBody}</tbody>
          </Table>
        </div>
      </div>

      <Modal show={Boolean(pendingAction)} onHide={closeConfirm} centered>
        <Modal.Header closeButton={!actionLoading}>
          <Modal.Title>
            {confirmAction === "release" ? "Release version" : "Rollback version"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRelease ? (
            <>
              <p className="mb-2">
                {confirmAction === "release"
                  ? `Release v${selectedRelease.version} to ${channel}? Students will receive this update.`
                  : `Rollback to v${selectedRelease.version} on ${channel}?`}
              </p>
              <p className="text-muted small mb-0">
                Platforms:{" "}
                {selectedRelease.platforms.map((p) => p.platform).join(", ")}
              </p>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={actionLoading} onClick={closeConfirm}>
            Cancel
          </Button>
          <Button
            variant={confirmAction === "release" ? "primary" : "warning"}
            disabled={actionLoading}
            onClick={() => void handleConfirm()}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Working…
              </>
            ) : confirmAction === "release" ? (
              "Release"
            ) : (
              "Rollback"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DesktopAppReleases;
